import { Component, effect } from '@angular/core';
import { SidebarService } from '@shared/services/sidebar.service';
import { CommonModule } from '@angular/common';
import { BackdropComponent } from '../backdrop/backdrop.component';
import { RouterModule } from '@angular/router';
import { AppHeaderComponent } from '../app-header/app-header.component';
import { ModalHostComponent } from '@shared/components/ui/modal-host/modal-host.component';
import { UserPreferencesService, UserPreferences } from '@shared/services/usuario/user-preferences.service';
import { ThemeService } from '@shared/services/theme.service';
import { NavLayoutService } from '@shared/services/nav-layout.service';
import { AppSidebarMenuComponent } from '../app-sidebar/app-sidebar-menu/app-sidebar-menu.component';
import { AppTopNavComponent } from '../app-top-nav/app-top-nav.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AppHeaderComponent,
    BackdropComponent,
    ModalHostComponent,
    AppSidebarMenuComponent,
    AppTopNavComponent,
  ],
  templateUrl: './app-layout.component.html',
})
export class AppLayoutComponent {
  readonly isExpanded$;
  readonly isHovered$;
  readonly isMobileOpen$;

  /** Disposición del menú activa (`sidebar` | `topnav`) — controla el template. */
  readonly navLayout;

  constructor(
    public sidebarService: SidebarService,
    private userPreferencesService: UserPreferencesService,
    private themeService: ThemeService,
    private navLayoutService: NavLayoutService
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isHovered$ = this.sidebarService.isHovered$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.navLayout = this.navLayoutService.mode;

    // Disposición del menú: localStorage primero (sin parpadeo en F5). El tema NO se toma de
    // aquí: ThemeService ya lo aplicó desde la clave `theme` (la misma que usa el login).
    const localPrefs = localStorage.getItem('userPreferences');
    if (localPrefs) {
      const prefs: UserPreferences = JSON.parse(localPrefs);
      if (prefs.navLayout) {
        this.navLayoutService.setMode(prefs.navLayout);
      }
    }

    // Reconcilia navLayout con el backend. El tema del login (o del toggle in-app) manda:
    // aplicar prefs.theme aquí volvía a dark/light al entrar aunque en login se hubiera
    // elegido lo contrario.
    effect(() => {
      const prefs = this.userPreferencesService.preferences();

      if (prefs?.navLayout) {
        this.navLayoutService.setMode(prefs.navLayout);
      }
      if (prefs) {
        localStorage.setItem('userPreferences', JSON.stringify(prefs));
      }
    });

    this.userPreferencesService.getPreferences().subscribe({
      next: prefs => this.persistLoginTheme(prefs),
      error: err => console.warn('No se pudieron cargar las preferencias del usuario', err),
    });
  }

  /**
   * Conserva el tema activo en login (localStorage `theme` / ThemeService) y lo persiste
   * en las preferencias del usuario si el backend tenía otro valor.
   */
  private persistLoginTheme(prefs: UserPreferences | null): void {
    const currentTheme = this.themeService.theme();
    if (prefs?.theme === currentTheme) {
      return;
    }

    const payload: UserPreferences = {
      ...(prefs ?? { theme: currentTheme, language: null, notificationsEnabled: true }),
      theme: currentTheme,
    };
    this.userPreferencesService.updatePreferences(payload).subscribe({
      error: err => console.warn('No se pudo sincronizar el tema del login', err),
    });
  }

  get containerClasses() {
    return [
      'flex-1',
      'transition-all',
      'duration-300',
      'ease-in-out',
      (this.isExpanded$ || this.isHovered$) ? 'xl:ml-[290px]' : 'xl:ml-[90px]',
      this.isMobileOpen$ ? 'ml-0' : ''
    ];
  }
}